import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Check, X, Mic2, Video, ClipboardCheck, Award, Users, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SalesMode from "@/components/SalesMode";
import {
  getTraining, markTrainingAttendance, setTrainingZoomLink, setTrainingSettings, setTrainingSessionLink, joinTraining, trainingZoomCheck, submitTrainingHomework, confirmTrainingHomework,
  logTrainingRoleplay, recordTrainingQuiz, certifyTraining, editTrainingList, getTrainingGymStaff, getMyPracticeRecordings,
} from "@/lib/api";

// Sales Training: the 12-session series on the 6 Week Challenge script.
// Reps see their own progress and hand in homework; managers track their gym's team, log the
// weekly in-gym role play, quiz scores and certification; the trainer marks Zoom attendance.

const CERT_PARTS = ["Opening and framing", "Discovery", "Summary and fit", "Three pillars", "Commitments",
  "Price and guarantee", "Close", "Objections"];
const DEPTH = { not_asked: "Didn't ask why", surface: "Surface", one_level_deeper: "One level deeper", real_pain: "Real pain" };

// Session start in IST from the series' session time (the trainer can change it).
const startAt = (program, s) => new Date(`${s.date}T${program.session_time || "17:00"}:00+05:30`);

// Join opens the Zoom link and is how attendance is taken. The tab is opened first, inside the tap,
// so phones don't block it as a pop-up; then it's pointed at the link FPA returns.
async function joinSession() {
  const w = window.open("", "_blank");
  try {
    const r = await joinTraining();
    if (w) w.location.href = r.url; else window.location.href = r.url;
    toast.success(r.marked ? (r.late ? "You're in. Marked as joined late." : "You're in. Attendance marked.") : "Opening Zoom");
  } catch (e) {
    if (w) w.close();
    err(e, "Couldn't open the session");
  }
}

const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);

const ScoreSelect = ({ value, onChange, max = 5, min = 1, placeholder = "—", testid }) => (
  <select value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} data-testid={testid}
    className="h-9 rounded-md bg-[#0a0a0a] border border-[#262626] px-2 text-sm text-zinc-100">
    <option value="">{placeholder}</option>
    {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => <option key={n} value={n}>{n}</option>)}
  </select>
);

export default function Training() {
  const [data, setData] = useState(null);
  const [allGyms, setAllGyms] = useState(false);
  const [dialog, setDialog] = useState(null);       // {kind, ...}
  const [practice, setPractice] = useState(false);

  const load = useCallback(() => getTraining(allGyms).then(setData).catch((e) => err(e, "Couldn't load training")), [allGyms]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>;
  const { program, role, me, roster } = data;
  const now = new Date(data.now);
  const startOf = (s) => startAt(program, s);
  const next = program.sessions.find((s) => startOf(s) > now);
  const live = program.sessions.find((s) => now >= new Date(startOf(s) - 15 * 60000) && now <= new Date(+startOf(s) + 30 * 60000));
  const held = program.sessions.filter((s) => startOf(s) <= now);
  const isTrainer = role === "trainer";
  const isManager = role === "manager" || isTrainer;

  return (
    <div className="space-y-5" data-testid="training-page">
      {/* Series header */}
      <section className="card-surface p-5 sm:p-6">
        <div className="eyebrow">Sales training series</div>
        <h2 className="font-display text-xl sm:text-2xl font-bold mt-1">{program.name}</h2>
        <p className="text-sm text-zinc-400 mt-1">
          12 live Zoom sessions · {program.days_label || "Mon, Wed, Fri"} · {program.time_label} · 30 minutes · trainer {program.trainer}
        </p>
        <p className="text-xs text-zinc-500 mt-1">Always join with the button below: that&apos;s how your attendance is marked. Zoom confirms who stayed afterwards.</p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {next ? (
            <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-sm">
              <span className="text-blue-300 font-medium">Next: Session {next.n}</span>
              <span className="text-zinc-300"> · {fmtDay(next.date)}, {program.time_label} · {next.title}</span>
            </div>
          ) : <div className="text-sm text-emerald-300">The series is complete.</div>}
          <Button onClick={joinSession} disabled={!live} className="rounded-full bg-blue-600 hover:bg-blue-500" data-testid="training-zoom">
            <Video className="h-4 w-4 mr-1.5" />{live ? `Join session ${live.n}` : "Join opens 15 min before"}</Button>
          <Link to="/improvement?tab=sales-training&view=practice" data-testid="training-practice-link">
            <Button variant="outline" className="rounded-full border-[#262626]"><Mic2 className="h-4 w-4 mr-1.5" />Script practice</Button>
          </Link>
          {isTrainer && (
            <Button variant="outline" className="rounded-full border-[#262626]" onClick={() => setDialog({ kind: "zoom", link: program.zoom_link || "" })}>
              Session settings
            </Button>
          )}
        </div>
      </section>

      {me && <MyProgress program={program} me={me} now={now} slots={data.roleplay_slots || []} onHandIn={(s) => setDialog({ kind: "homework", session: s })}
        onPractice={() => setPractice(true)} />}

      {isManager && roster && (
        <TeamTracker program={program} roster={roster} held={held} isTrainer={isTrainer} allGyms={allGyms} setAllGyms={setAllGyms}
          slots={data.roleplay_slots || []} now={now} open={setDialog} />
      )}

      {!me && !isManager && (
        <section className="card-surface p-5 text-sm text-zinc-400">
          You&apos;re not on the training list. If you sell consults, ask your facility head to add you.
        </section>
      )}

      {/* Schedule */}
      <section className="card-surface p-5 sm:p-6">
        <div className="eyebrow">Schedule</div>
        <ol className="mt-3 divide-y divide-[#1f1f1f]">
          {program.sessions.map((s) => (
            <li key={s.n} className="py-2.5 flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 text-sm">
              <span className="text-zinc-500 w-36 shrink-0"><CalendarDays className="inline h-3.5 w-3.5 mr-1" />{fmtDay(s.date)} · S{s.n}</span>
              <span className="text-zinc-200 flex-1">{s.title}</span>
              <span className="text-xs text-zinc-500 sm:w-64">Homework: {s.homework}
                {s.roleplay_date && <span className="block text-emerald-400/80">In-gym role play {fmtDay(s.roleplay_date)}</span>}</span>
            </li>
          ))}
        </ol>
        <p className="text-xs text-zinc-500 mt-3">
          The day after each online session, your facility head runs an in-gym role play on what was just taught. The last one is the certification role play.
        </p>
      </section>

      {practice && (
        <SalesMode practice onClose={() => setPractice(false)}
          onSaved={(r) => { setPractice(false); setDialog({ kind: "homework", session: (held[held.length - 1] || program.sessions[0]).n, recordingId: r.id }); }} />
      )}

      <Dialogs dialog={dialog} close={() => setDialog(null)} reload={load} program={program} roster={roster || []} held={held} />
    </div>
  );
}

function MyProgress({ program, me, now, slots, onHandIn, onPractice }) {
  const { record, summary } = me;
  const hw = record.homework || {};
  const att = record.attended || {};
  const startOf = (s) => startAt(program, s);
  return (
    <section className="card-surface p-5 sm:p-6" data-testid="training-me">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="eyebrow">My progress</div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Stat label="Sessions attended" value={`${summary.attended}/${summary.attendance_taken}`} />
            <Stat label="Homework handed in" value={`${summary.homework_submitted}`} warn={summary.homework_overdue.length > 0}
              sub={summary.homework_overdue.length ? `${summary.homework_overdue.length} overdue` : null} />
            <Stat label="Gym role plays" value={`${Object.keys(summary.roleplays).length}/${slots.filter((x) => new Date(x.due) <= now).length || "—"}`}
              warn={summary.roleplays_missed.length > 0} sub={summary.roleplays_missed.length ? `${summary.roleplays_missed.length} missed` : null} />
            <Stat label="Quiz" value={summary.quiz ? `${summary.quiz.score}/10` : "—"} />
            <Stat label="Certified" value={summary.certification ? (summary.certification.passed ? "Yes" : "Not yet") : "—"} />
          </div>
        </div>
        <Button onClick={onPractice} className="rounded-full bg-rose-600 hover:bg-rose-500" data-testid="training-practice">
          <Mic2 className="h-4 w-4 mr-1.5" />Record a practice role play
        </Button>
      </div>
      <ul className="mt-4 space-y-1.5">
        {program.sessions.filter((s) => startOf(s) <= now).map((s) => {
          const h = hw[s.n];
          const overdue = summary.homework_overdue.includes(s.n);
          return (
            <li key={s.n} className="flex flex-wrap items-center gap-2 text-sm rounded-lg border border-[#1f1f1f] px-3 py-2">
              <span className="text-zinc-500 w-10">S{s.n}</span>
              <span className="text-zinc-200 flex-1 min-w-[10rem]">{s.title}</span>
              {s.attendance_taken && (att[s.n] ? <span className="text-emerald-300 text-xs"><Check className="inline h-3.5 w-3.5" /> attended
                {record.joins?.[s.n]?.late ? " (late)" : ""}{record.zoom?.[s.n]?.minutes != null ? ` · ${record.zoom[s.n].minutes} min` : ""}</span>
                : <span className="text-rose-300 text-xs"><X className="inline h-3.5 w-3.5" /> {record.zoom?.[s.n]?.status === "left_early" ? `left early (${record.zoom[s.n].minutes} min)` : "missed"}</span>)}
              {h ? <span className={`text-xs ${h.confirmed_at ? "text-emerald-300" : "text-amber-300"}`}>
                {h.confirmed_at ? "Homework confirmed" : "Homework handed in"}</span>
                : <Button size="sm" variant={overdue ? "default" : "outline"} onClick={() => onHandIn(s.n)}
                  className={`rounded-full h-8 ${overdue ? "bg-amber-600 hover:bg-amber-500" : "border-[#262626]"}`}>
                  {overdue ? "Overdue · hand in" : "Hand in homework"}</Button>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

const Stat = ({ label, value, sub, warn }) => (
  <div className={`rounded-lg border px-3 py-2 ${warn ? "border-amber-500/40 bg-amber-500/10" : "border-[#262626] bg-[#0a0a0a]"}`}>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className="font-display font-bold text-zinc-100">{value}</div>
    {sub && <div className="text-amber-300">{sub}</div>}
  </div>
);

function TeamTracker({ program, roster, held, isTrainer, allGyms, setAllGyms, slots, now, open }) {
  const label = Object.fromEntries(slots.map((x) => [x.key, x.label]));
  const dueNow = slots.filter((x) => new Date(x.due) <= now);
  const toConfirm = roster.reduce((n, r) => n + r.summary.homework_to_confirm.length, 0);
  return (
    <section className="card-surface p-5 sm:p-6" data-testid="training-team">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="eyebrow">{allGyms ? "All gyms" : "My team"}</div>
          <h3 className="font-display text-lg font-bold">{roster.length} on the training list</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {isTrainer && (
            <>
              <Button variant="outline" className="rounded-full border-[#262626]" onClick={() => setAllGyms(!allGyms)}>
                {allGyms ? "Just this gym" : "All gyms"}
              </Button>
              <Button className="rounded-full bg-blue-600 hover:bg-blue-500" onClick={() => open({ kind: "attendance" })} disabled={!held.length}>
                <Video className="h-4 w-4 mr-1.5" />Mark Zoom attendance
              </Button>
            </>
          )}
          <Button variant="outline" className="rounded-full border-[#262626]" onClick={() => open({ kind: "confirm" })} disabled={!toConfirm}>
            <ClipboardCheck className="h-4 w-4 mr-1.5" />Confirm homework{toConfirm ? ` (${toConfirm})` : ""}
          </Button>
          <Button variant="outline" className="rounded-full border-[#262626]" onClick={() => open({ kind: "roleplay", slots })}>Log role play</Button>
          <Button variant="outline" className="rounded-full border-[#262626]" onClick={() => open({ kind: "quiz" })}>Quiz scores</Button>
          <Button variant="outline" className="rounded-full border-[#262626]" onClick={() => open({ kind: "certify" })}>
            <Award className="h-4 w-4 mr-1.5" />Certify
          </Button>
          {!allGyms && (
            <Button variant="outline" className="rounded-full border-[#262626]" onClick={() => open({ kind: "list" })}>
              <Users className="h-4 w-4 mr-1.5" />Edit list
            </Button>
          )}
        </div>
      </div>
      <div className="mt-4 overflow-x-auto -mx-5 sm:mx-0">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-zinc-500">
            <tr className="text-left">
              <th className="py-2 px-3">Rep</th>
              <th className="py-2 px-3">Zoom</th>
              <th className="py-2 px-3">Homework</th>
              <th className="py-2 px-3">Role plays W1–W3</th>
              <th className="py-2 px-3">Quiz</th>
              <th className="py-2 px-3">Certified</th>
              <th className="py-2 px-3">Real calls</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1f1f1f]">
            {roster.map((r) => {
              const s = r.summary;
              return (
                <tr key={r.user_id} data-testid="training-row">
                  <td className="py-2.5 px-3">
                    <div className="text-zinc-100">{r.name}</div>
                    {allGyms && <div className="text-xs text-zinc-500">{r.gym}</div>}
                  </td>
                  <td className={`py-2.5 px-3 ${s.missed_sessions.length ? "text-amber-300" : "text-zinc-300"}`}>
                    {s.attended}/{s.attendance_taken}
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="text-zinc-300">{s.homework_confirmed} confirmed</span>
                    {s.homework_to_confirm.length > 0 && <span className="text-blue-300"> · {s.homework_to_confirm.length} to check</span>}
                    {s.homework_overdue.length > 0 && <span className="text-amber-300"> · {s.homework_overdue.length} overdue</span>}
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="text-zinc-300 tabular-nums">{Object.keys(s.roleplays).length}/{dueNow.length}</span>
                    {s.roleplays_missed.length > 0 && <span className="text-rose-300 text-xs"> · missed {s.roleplays_missed.map((k) => label[k] || k).join(", ")}</span>}
                  </td>
                  <td className="py-2.5 px-3 text-zinc-300">{s.quiz ? <>{s.quiz.score}/10
                    {s.quiz.attempts?.length > 1 && <span className="text-xs text-zinc-500"> · {s.quiz.attempts.length} tries</span>}</> : "—"}</td>
                  <td className="py-2.5 px-3">{s.certification ? (s.certification.passed
                    ? <span className="text-emerald-300">Yes ({s.certification.avg})</span>
                    : <span className="text-amber-300">Not yet ({s.certification.avg})</span>) : <span className="text-zinc-500">—</span>}</td>
                  <td className="py-2.5 px-3 text-zinc-300">
                    {r.real_calls.rated ? <>{r.real_calls.avg} avg · {r.real_calls.rated} rated
                      {r.real_calls.last_depth && <div className="text-xs text-zinc-500">Discovery: {DEPTH[r.real_calls.last_depth]}</div>}</>
                      : <span className="text-zinc-500">none yet</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-zinc-500 mt-3">
        Real calls = consult recordings rated by FPA since the series started (practice role plays excluded). <Link to="/recordings" className="text-blue-400">Recordings →</Link>
      </p>
    </section>
  );
}

function Dialogs({ dialog, close, reload, program, roster, held }) {
  const kind = dialog?.kind;
  const done = async (p, msg) => {
    try { await p; toast.success(msg); close(); reload(); } catch (e) { err(e, "Couldn't save"); }
  };
  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-lg max-h-[90vh] overflow-y-auto">
        {kind === "zoom" && <ZoomForm dialog={dialog} done={done} program={program} />}
        {kind === "homework" && <HomeworkForm dialog={dialog} done={done} program={program} />}
        {kind === "attendance" && <AttendanceForm done={done} roster={roster} held={held} />}
        {kind === "confirm" && <ConfirmForm roster={roster} program={program} reload={reload} />}
        {kind === "roleplay" && <RoleplayForm done={done} roster={roster} slots={dialog.slots || []} />}
        {kind === "quiz" && <QuizForm done={done} roster={roster} />}
        {kind === "certify" && <CertifyForm done={done} roster={roster} />}
        {kind === "list" && <ListForm roster={roster} reload={reload} />}
      </DialogContent>
    </Dialog>
  );
}

const Head = ({ title, desc }) => (
  <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle>{desc && <DialogDescription className="text-zinc-400">{desc}</DialogDescription>}</DialogHeader>
);

function ZoomForm({ dialog, done, program }) {
  const [link, setLink] = useState(dialog.link);
  const [time, setTime] = useState(program?.session_time || "17:00");
  const [n, setN] = useState("");
  const [one, setOne] = useState("");
  return (<>
    <Head title="Session settings" desc="Reps only ever see a Join button; the link stays in FPA so attendance is marked." />
    <div className="text-xs text-zinc-500">Zoom link for every session</div>
    <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://zoom.us/j/…" className="bg-[#0a0a0a] border-[#262626]" />
    <div className="text-xs text-zinc-500">Start time (IST, 24-hour). Dubai is 1h30 earlier; Sri Lanka is the same.</div>
    <Input value={time} onChange={(e) => setTime(e.target.value)} placeholder="10:15" className="bg-[#0a0a0a] border-[#262626] w-28" />
    <Button className="rounded-full bg-blue-600 hover:bg-blue-500" onClick={() => done(setTrainingSettings({ zoom_link: link, session_time: time }), "Saved")}>Save</Button>
    <div className="border-t border-[#262626] pt-3 text-xs text-zinc-500">A different link for one session (optional)</div>
    <select value={n} onChange={(e) => { setN(e.target.value); setOne(program.sessions.find((s) => String(s.n) === e.target.value)?.zoom_link || ""); }}
      className="h-10 rounded-md bg-[#0a0a0a] border border-[#262626] px-2 text-sm">
      <option value="">Pick a session</option>
      {program.sessions.map((s) => <option key={s.n} value={s.n}>Session {s.n} · {fmtDay(s.date)}{s.zoom_link ? " · own link" : ""}</option>)}
    </select>
    {n && <>
      <Input value={one} onChange={(e) => setOne(e.target.value)} placeholder="Leave empty to use the link above" className="bg-[#0a0a0a] border-[#262626]" />
      <Button variant="outline" className="rounded-full border-[#262626]" onClick={() => done(setTrainingSessionLink(Number(n), one), "Session link saved")}>Save for session {n}</Button>
    </>}
  </>);
}

function HomeworkForm({ dialog, done, program }) {
  const [session, setSession] = useState(dialog.session);
  const [note, setNote] = useState("");
  const [rec, setRec] = useState(dialog.recordingId || "");
  const [recs, setRecs] = useState([]);
  useEffect(() => { getMyPracticeRecordings().then(setRecs).catch(() => setRecs([])); }, []);
  const s = program.sessions.find((x) => x.n === session);
  return (<>
    <Head title="Hand in homework" desc={s?.homework} />
    <select value={session} onChange={(e) => setSession(Number(e.target.value))} className="h-10 rounded-md bg-[#0a0a0a] border border-[#262626] px-2 text-sm">
      {program.sessions.map((x) => <option key={x.n} value={x.n}>Session {x.n}: {x.title}</option>)}
    </select>
    <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="What you did, or paste your written homework here"
      className="bg-[#0a0a0a] border-[#262626]" />
    <div>
      <div className="text-xs text-zinc-500 mb-1">Attach a practice recording (optional)</div>
      <select value={rec} onChange={(e) => setRec(e.target.value)} className="w-full h-10 rounded-md bg-[#0a0a0a] border border-[#262626] px-2 text-sm">
        <option value="">— none —</option>
        {recs.map((r) => <option key={r.id} value={r.id}>{r.title}{r.recorded_seconds ? ` · ${Math.round(r.recorded_seconds / 60)} min` : ""}</option>)}
      </select>
    </div>
    <Button className="rounded-full bg-blue-600 hover:bg-blue-500" onClick={() => done(submitTrainingHomework(session, note, rec), "Homework handed in")}>Hand in</Button>
  </>);
}

function AttendanceForm({ done, roster, held }) {
  const [session, setSession] = useState(held[held.length - 1]?.n);
  const [present, setPresent] = useState(() => new Set());
  useEffect(() => {
    setPresent(new Set(roster.filter((r) => r.record.attended?.[session]).map((r) => r.user_id)));
  }, [session, roster]);
  const toggle = (id) => setPresent((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  return (<>
    <Head title="Zoom attendance" desc="Marked automatically when people join from FPA, then checked against Zoom's report after the session. Change anyone here; your marks win." />
    <select value={session} onChange={(e) => setSession(Number(e.target.value))} className="h-10 rounded-md bg-[#0a0a0a] border border-[#262626] px-2 text-sm">
      {held.map((s) => <option key={s.n} value={s.n}>Session {s.n} · {fmtDay(s.date)}</option>)}
    </select>
    <div className="max-h-80 overflow-y-auto space-y-1">
      {roster.map((r) => (
        <label key={r.user_id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-[#141414] text-sm">
          <input type="checkbox" checked={present.has(r.user_id)} onChange={() => toggle(r.user_id)} />
          <span className="flex-1">{r.name}</span><span className="text-xs text-zinc-500">{r.gym}</span>
        </label>
      ))}
    </div>
    <p className="text-xs text-zinc-500">Tip: switch the team view to "All gyms" first so everyone is listed.</p>
    <Button variant="outline" className="rounded-full border-[#262626]" onClick={() => trainingZoomCheck(session)
      .then((r) => toast.success(r.checked ? `Zoom checked: ${r.attended} attended, ${r.left_early} left early, ${r.not_found} not found in Zoom` : r.reason))
      .catch((e) => err(e, "Couldn't check Zoom"))}>Check Zoom now</Button>
    <Button className="rounded-full bg-blue-600 hover:bg-blue-500" onClick={() => done(markTrainingAttendance(session, [...present]), "Attendance saved")}>
      Save ({present.size} present)
    </Button>
  </>);
}

function ConfirmForm({ roster, program, reload }) {
  const items = roster.flatMap((r) => r.summary.homework_to_confirm.map((n) => ({ r, n, h: r.record.homework[n] })));
  const confirm = async (r, n) => {
    try { await confirmTrainingHomework(r.user_id, n); toast.success(`Confirmed ${r.name}, session ${n}`); reload(); }
    catch (e) { err(e, "Couldn't confirm"); }
  };
  return (<>
    <Head title="Homework to check" desc="Read or listen, then confirm it's done properly." />
    {items.length === 0 && <p className="text-sm text-zinc-500">Nothing waiting.</p>}
    <ul className="space-y-2">
      {items.map(({ r, n, h }) => (
        <li key={`${r.user_id}-${n}`} className="rounded-lg border border-[#262626] p-3 text-sm">
          <div className="flex items-center justify-between gap-2">
            <span className="text-zinc-100">{r.name} · S{n}</span>
            <Button size="sm" className="rounded-full h-8 bg-emerald-600 hover:bg-emerald-500" onClick={() => confirm(r, n)}>Confirm</Button>
          </div>
          <div className="text-xs text-zinc-500 mt-1">{program.sessions[n - 1]?.homework}</div>
          {h?.note && <p className="text-zinc-300 mt-1.5 whitespace-pre-wrap">{h.note}</p>}
          {h?.recording_id && <Link to={`/recordings/${h.recording_id}`} className="text-blue-400 text-xs">Listen to the recording →</Link>}
        </li>
      ))}
    </ul>
  </>);
}

function RoleplayForm({ done, roster, slots }) {
  // Default to the most recent role play that has happened (or the next one).
  const past = slots.filter((x) => new Date(x.due) - 86400000 <= Date.now());
  const [key, setKey] = useState((past[past.length - 1] || slots[0])?.key || "s1");
  const [scores, setScores] = useState({});
  const [note, setNote] = useState("");
  const picked = Object.fromEntries(Object.entries(scores).filter(([, v]) => v));
  return (<>
    <Head title="In-gym role play" desc="Score each rep who played coach, 1 (needs work) to 5 (ready)." />
    <select value={key} onChange={(e) => setKey(e.target.value)} className="h-10 rounded-md bg-[#0a0a0a] border border-[#262626] px-2 text-sm">
      {slots.map((x) => <option key={x.key} value={x.key}>{x.label}{x.date ? ` · ${fmtDay(x.date)}` : ""} · {x.focus}</option>)}
    </select>
    <ul className="space-y-1.5">
      {roster.map((r) => (
        <li key={r.user_id} className="flex items-center justify-between gap-2 text-sm">
          <span>{r.name}</span>
          <ScoreSelect value={scores[r.user_id]} onChange={(v) => setScores((s) => ({ ...s, [r.user_id]: v }))} />
        </li>
      ))}
    </ul>
    <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Notes (optional)" className="bg-[#0a0a0a] border-[#262626]" />
    <Button className="rounded-full bg-blue-600 hover:bg-blue-500" disabled={!Object.keys(picked).length}
      onClick={() => done(logTrainingRoleplay(key, picked, note), "Role play logged")}>Save {Object.keys(picked).length} scores</Button>
  </>);
}

function QuizForm({ done, roster }) {
  const [rep, setRep] = useState(roster[0]?.user_id || "");
  const [score, setScore] = useState(null);
  return (<>
    <Head title="Rules quiz (Session 6)" desc="10 questions on prices, guarantee conditions and refunds. 9/10 passes." />
    <RepSelect roster={roster} value={rep} onChange={setRep} />
    <ScoreSelect value={score} onChange={setScore} min={0} max={10} placeholder="Score /10" />
    <Button className="rounded-full bg-blue-600 hover:bg-blue-500" disabled={score == null || !rep}
      onClick={() => done(recordTrainingQuiz(rep, score), "Quiz score saved")}>Save</Button>
  </>);
}

function CertifyForm({ done, roster }) {
  const [rep, setRep] = useState(roster[0]?.user_id || "");
  const [scores, setScores] = useState(Array(8).fill(null));
  const filled = scores.every((s) => s);
  const avg = filled ? scores.reduce((a, b) => a + b, 0) / 8 : null;
  const pass = filled && avg >= 4 && Math.min(...scores) >= 3;
  return (<>
    <Head title="Certification role play" desc="A full call with you as the member. Pass = average 4 or more, with no part below 3." />
    <RepSelect roster={roster} value={rep} onChange={setRep} />
    <ul className="space-y-1.5">
      {CERT_PARTS.map((p, i) => (
        <li key={p} className="flex items-center justify-between gap-2 text-sm">
          <span>{i + 1}. {p}</span>
          <ScoreSelect value={scores[i]} onChange={(v) => setScores((s) => s.map((x, j) => (j === i ? v : x)))} />
        </li>
      ))}
    </ul>
    {filled && <p className={`text-sm ${pass ? "text-emerald-300" : "text-amber-300"}`}>Average {avg.toFixed(2)} · {pass ? "passes" : "not yet"}</p>}
    <Button className="rounded-full bg-blue-600 hover:bg-blue-500" disabled={!filled || !rep}
      onClick={() => done(certifyTraining(rep, scores), pass ? "Certified" : "Saved: not passed yet")}>Save</Button>
  </>);
}

const RepSelect = ({ roster, value, onChange }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="h-10 rounded-md bg-[#0a0a0a] border border-[#262626] px-2 text-sm">
    {roster.map((r) => <option key={r.user_id} value={r.user_id}>{r.name}{r.gym ? ` · ${r.gym}` : ""}</option>)}
  </select>
);

function ListForm({ roster, reload }) {
  const [staff, setStaff] = useState(null);
  useEffect(() => { getTrainingGymStaff().then(setStaff).catch(() => setStaff([])); }, []);
  const on = useMemo(() => new Set(roster.map((r) => r.user_id)), [roster]);
  const flip = async (u) => {
    try { await editTrainingList(u.user_id, !on.has(u.user_id)); reload(); } catch (e) { err(e, "Couldn't update the list"); }
  };
  return (<>
    <Head title="Who's on the training list" desc="Everyone with the sales skill is added automatically. Add or remove people here." />
    {!staff ? <Loader2 className="h-4 w-4 animate-spin" /> : (
      <ul className="space-y-1 max-h-96 overflow-y-auto">
        {staff.sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((u) => (
          <li key={u.user_id} className="flex items-center justify-between gap-2 text-sm rounded px-2 py-1.5 hover:bg-[#141414]">
            <span>{u.name}</span>
            <Button size="sm" variant="outline" className="h-8 rounded-full border-[#262626]" onClick={() => flip(u)}>
              {on.has(u.user_id) ? "Remove" : "Add"}
            </Button>
          </li>
        ))}
      </ul>
    )}
  </>);
}
